import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import ChipGroup from '@/components/inputs/ChipGroup';
import FileInputField from '@/components/inputs/FileInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { ThirdPartyAuditData, thirdPartyAuditResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

interface NpoPartyAuditFormProps {
  initialData:
    | {
        thirdPartyAudit: string | undefined;
        thirdPartyAuditDeny: boolean | undefined;
      }
    | undefined;
  isLoading: boolean;
  showBackButton?: boolean;
  showSaveAndContinueButton?: boolean;
  submitLabel?: string;
  fileProgress?: number;
  onSubmitTapped: (data: ThirdPartyAuditData) => void;
}

const NpoPartyAuditForm = ({
  initialData,
  isLoading,
  showBackButton = true,
  showSaveAndContinueButton = true,
  submitLabel = i18n.t('mix.next'),
  fileProgress,
  onSubmitTapped
}: NpoPartyAuditFormProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, submitCount }
  } = useForm<ThirdPartyAuditData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: thirdPartyAuditResolver as any
  });

  const onSubmit: SubmitHandler<ThirdPartyAuditData> = async (data) => {
    onSubmitTapped(data);
  };

  const thirdPartyAuditDenyListener = watch('thirdPartyAuditDeny');

  useEffect(() => {
    if (!thirdPartyAuditDenyListener) setValue('thirdPartyAudit', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdPartyAuditDenyListener]);

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <View style={styles.fields}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.party_audit.title')}
            description={i18n.t('sign_in.nonProfit.party_audit.description')}
            infoButtonLabel={i18n.t('sign_in.nonProfit.party_audit.info')}>
            <View style={styles.halfWidth}>
              <ChipGroup
                control={control}
                name="thirdPartyAuditDeny"
                options={[
                  { label: capitalize(i18n.t('mix.yes')), value: true },
                  { label: capitalize(i18n.t('mix.no')), value: false }
                ]}
              />
            </View>
            {thirdPartyAuditDenyListener && (
              <FileInputField control={control} name="thirdPartyAudit" progress={fileProgress} />
            )}
          </OnboardingSection>
        </View>

        <View style={styles.buttonsContainer}>
          <View style={styles.nextBackContainer}>
            {showBackButton && router.canGoBack() && (
              <BrandedButton disabled={isLoading} style={styles.flexOne} onPress={router.back}>
                {capitalize(i18n.t('mix.back'))}
              </BrandedButton>
            )}
            <BrandedButton
              style={styles.flexOne}
              loading={isLoading}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {submitLabel}
            </BrandedButton>
          </View>
          {showSaveAndContinueButton && <SaveAndContinueLaterButton disabled={isLoading} />}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  fields: {
    flexDirection: 'column',
    gap: 25
  },

  flexOne: {
    flex: 1
  },

  halfWidth: {
    width: '50%'
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column',
    gap: 10
  }
});

export default NpoPartyAuditForm;
